import React, { useRef, useState } from 'react'
import { filter, get } from 'lodash-es'

import Card from '@mui/material/Card'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material'

import SubSubCategory from './SubSubCategory'
import Link from '../Common/Link'

import { Category } from '@/src/types/api-types'
import pushRoute from '@/lib/pushRoute'
import getRouteByBrand from '@/lib/getRouteByBrand'
import getRouteBrandByLetter from '@/lib/getRouteBrandByLetter'
import getRoutes from '@/lib/getRoutes'

type CategoryExtended = Category & {
  mode?: string
}

type Props = {
  category: CategoryExtended
  categories: Category[]
  mobile?: boolean
  index?: number
}

const chunk = (arr, size) =>
  arr.reduce((acc, _, i) => {
    if (i % size === 0) acc.push(arr.slice(i, i + size))
    return acc
  }, [])

const SubCategory: React.FC<Props> = ({ category, categories, mobile, index }) => {
  const theme = useTheme()
  const [open, setOpen] = useState<boolean>(false)
  const linkRef = useRef(null)

  const handleMouseEnter = () => {
    setOpen(true)
  }

  const handleMouseLeave = () => {
    setOpen(false)
  }

  const handleClick = () => {
    setOpen(!open)
  }

  const handleClickBrand = brand => () => {
    const featuresHash = `Marques-${brand?.category ?? ''}`
    const route = getRoutes({
      route: 'Catalogsearch',
      argsObject: {
        query: '',
        featuresHash,
      },
    })
    return pushRoute(route)
  }

  // On recupere les sous catégories
  // quand on clique sur une catégorie on séléctionne
  const getFeaturesHash = cat => {
    // Récupérer toutes les sous-catégories directes
    const directSubCategories = filter(
      categories,
      c => Number(c.parent_id) === Number(cat.category_id)
    )

    // Créer la liste des catégories à inclure (la catégorie mère + ses sous-catégories)
    const categoriesToInclude = [
      cat.category,
      ...directSubCategories.map(subCat => subCat.category),
    ]

    // Créer le featuresHash avec toutes les catégories
    return categoriesToInclude.map(catName => `Catégories-${catName}`).join('_')
  }

  const handleClickBrandLetter = args => async () => {
    return pushRoute(await getRouteBrandByLetter(args))
  }

  const subCategories = filter(
    categories,
    c => Number(c.parent_id) === Number(category.category_id)
  ).sort((a, b) => {
    if (a?.position === b?.position) return 0
    return a.position > b.position ? 1 : -1
  })

  if (mobile) {
    // Lien vers la destination de la categorie (ligne sans sous-menu + "Voir tout")
    const mobileCategoryLink = (label: string) =>
      category.mode === 'list' ? (
        <Link route={'Brands'}>
          <ListItemText primary={label} />
        </Link>
      ) : (
        <Link
          route="Catalogsearch"
          argsObject={{ query: '', featuresHash: getFeaturesHash(category) }}
          className="cursor-pointer"
        >
          <ListItemText primary={label} />
        </Link>
      )

    return (
      <>
        {subCategories.length > 0 ? (
          // Tap sur toute la ligne = deplier/replier (pas de navigation, sinon le
          // drawer se ferme via routeChangeStart)
          <ListItem button onClick={handleClick}>
            <ListItemText primary={category.category} />
            {open ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
        ) : (
          <ListItem button>{mobileCategoryLink(category.category)}</ListItem>
        )}
        <Divider />
        <Collapse in={open} timeout="auto" unmountOnExit style={{ paddingLeft: 16 }}>
          <ListItem button>{mobileCategoryLink(t({ id: 'web_see_all' }))}</ListItem>
          <Divider />
          {subCategories.map(subCat => (
            <SubSubCategory
              key={subCat.category_id}
              category={subCat}
              categories={categories}
              mobile
            />
          ))}
        </Collapse>
      </>
    )
  }

  const defaultRender = (
    <table>
      <tbody>
        {chunk(
          subCategories.map(subCat => (
            <SubSubCategory key={subCat.category_id} category={subCat} categories={categories} />
          )),
          2
        ).map((i, index) => (
          <tr key={index}>{i}</tr>
        ))}
      </tbody>
    </table>
  )

  let lastLetter = ''
  const listRender = (
    <div
      className="w-[900px] max-w-[calc(100vw-260px)] grid grid-cols-[repeat(auto-fill,215px)] overflow-y-auto"
      style={{ minHeight: 550, maxHeight: 'calc(100vh - 160px)' }}
    >
      {subCategories.map(subCat => {
        const firstLetter = subCat.category.substr(0, 1).toLowerCase()

        const letter =
          lastLetter !== firstLetter ? (
            <div className="text-[150%] font-semibold uppercase">{firstLetter}</div>
          ) : null

        // eslint-disable-next-line
        lastLetter = firstLetter
        return (
          <React.Fragment key={subCat.category_id}>
            {letter}
            <div
              className="font-normal w-[200px] text-ellipsis overflow-hidden whitespace-nowrap cursor-pointer hover:text-primary"
              style={{ '--tw-text-opacity': 1 } as React.CSSProperties}
              onClick={handleClickBrand(subCat)}
            >
              {subCat.category}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )

  return (
    <div onMouseLeave={handleMouseLeave}>
      <div ref={linkRef} onMouseEnter={handleMouseEnter}>
        {category.mode === 'list' ? (
          <div
            className="p-3 pl-4 min-w-[250px] border border-transparent cursor-pointer"
            style={
              open
                ? {
                    background: theme.palette.primary.light,
                    color: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                  }
                : undefined
            }
            onClick={handleClickBrandLetter(category.category.substr(0, 1))}
          >
            {category.category}
          </div>
        ) : (
          <Link
            route="Catalogsearch"
            argsObject={{ query: '', featuresHash: getFeaturesHash(category) }}
            className="cursor-pointer"
          >
            <div
              className="p-3 pl-4 min-w-[250px] border border-transparent"
              style={
                open
                  ? {
                      background: theme.palette.primary.light,
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.main}`,
                      cursor: 'pointer',
                    }
                  : undefined
              }
            >
              {category.category}
            </div>
          </Link>
        )}
      </div>
      {subCategories.length > 0 && open && (
        <Card
          style={{
            position: 'absolute',
            marginLeft: get(linkRef, 'current.clientWidth', 0),
            marginTop: -get(linkRef, 'current.clientHeight', 0) * (index + 1),
            zIndex: 1300,
            padding: 16,
            minHeight: get(linkRef, 'current.clientHeight', 0) * (index + 1),
          }}
        >
          {category.mode === 'list' ? listRender : defaultRender}
        </Card>
      )}
    </div>
  )
}

export default SubCategory
